import { yupResolver } from "@hookform/resolvers/yup";
import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSWRConfig } from "swr";
import { SubtypeModalForm } from "~/components/specifics/settings/subtypes/SubtypeModalForm";
import type { Subtype, SubtypeEdit } from "~/models/Subtype";
import { buildDefaultSubtype, validationSchema } from "~/models/Subtype";
import { isSuccess } from "~/utils/api/api_handler";
import { create, update } from "~/utils/api/Subtype";
import { onInvalidScroll } from "~/utils/FormUtil";
import { generatePath } from "~/utils/generatePath";

type Props = DeepReadonly<{
	handleClose: () => void;
	subtype?: Subtype | null;
}>;

export function SubtypeModal({ subtype = null, handleClose }: Props) {
	const { notify } = useNotification();
	const { mutate: globalMutate } = useSWRConfig();
	const methods = useForm<SubtypeEdit>({
		defaultValues: subtype ?? buildDefaultSubtype(),
		mode: "onBlur",
		resolver: yupResolver(validationSchema)
	});
	const handleModalSubmit = useCallback(() => {
		void methods.handleSubmit(
			async (submitValue: SubtypeEdit) => {
				if (subtype === null) {
					// create
					const response = await create(submitValue);
					if (isSuccess(response)) {
						await globalMutate(generatePath("/subtypes"));
						handleClose();
						notify({ content: "作成しました。", variant: "success" });
						return;
					}
					notify({ content: "エラーが発生しました。", variant: "error" });
					return;
				}
				// update
				const response = await update(subtype.id, submitValue);
				if (isSuccess(response)) {
					await globalMutate(generatePath("/subtypes"));
					handleClose();
					notify({ content: "更新しました。", variant: "success" });
					return;
				}
				notify({ content: "エラーが発生しました。", variant: "error" });
			},
			(errors) => {
				onInvalidScroll(errors);
			}
		)();
	}, [methods, subtype, notify, globalMutate, handleClose]);
	return (
		<Modalv2 isOpen onClose={handleClose}>
			<ModalHeader title={subtype ? "案件タイプ編集" : "案件タイプ新規作成"} />
			<ModalBody>
				<FormProvider {...methods}>
					<SubtypeModalForm />
				</FormProvider>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button onClick={handleClose} variant="secondary">
						保存せず閉じる
					</Button>
				}
				rightArea={
					<Button onClick={handleModalSubmit} variant="primary">
						保存
					</Button>
				}
			/>
		</Modalv2>
	);
}
